import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaSearch, FaUser, FaTimes } from "react-icons/fa";

import Modal from "Components/UI/Modal";
import Button from "Components/UI/Button";
import RichTextEditor from "Components/UI/RichTextEditor";
import {
  createWorkMemo,
  searchWorkMemoCustomer,
  searchUserForMemo,
} from "JS/API";

import { designTokens } from "utils/designSystem";
import { detailModalStyles } from "utils/modalDesignSystem";

// 채널 옵션 (API 문서에 맞춰 소문자 사용)
const CHANNEL_OPTIONS = [
  { value: 'markpick', label: '마크픽' },
  { value: 'kmong', label: '크몽' },
  { value: 'phone', label: '전화' },
  { value: 'email', label: '메일' },
  { value: 'kakao', label: '카톡' },
  { value: 'kakao_channel', label: '카톡채널' },
  { value: 'ceo_order', label: '대표님 지시' },
  { value: 'etc', label: '기타' },
];

// 전화번호 포맷팅 (자동 하이픈)
const formatPhoneNumber = (value) => {
  // 숫자만 추출
  const numbers = value.replace(/[^0-9]/g, '');

  // 최대 11자리로 제한
  const limited = numbers.slice(0, 11);

  // 하이픈 추가
  if (limited.length <= 3) {
    return limited;
  } else if (limited.length <= 7) {
    return `${limited.slice(0, 3)}-${limited.slice(3)}`;
  } else {
    return `${limited.slice(0, 3)}-${limited.slice(3, 7)}-${limited.slice(7)}`;
  }
};

// 전화번호에서 하이픈 제거
const removePhoneHyphens = (value) => {
  return value.replace(/-/g, '');
};

// 채널 한글명

const WorkMemoCreateModal = ({ onClose, onSuccess }) => {
  const navigate = useNavigate();

  // 현재 시간을 datetime-local 형식으로 반환
  const getCurrentDateTime = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // 폼 상태
  const [channel, setChannel] = useState('');
  const [memoAt, setMemoAt] = useState(getCurrentDateTime()); // 문의 시간 (기본: 현재)
  const [customerName, setCustomerName] = useState('');
  const [contact, setContact] = useState('');
  const [content, setContent] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);

  // 자동완성 상태
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [autocompleteResults, setAutocompleteResults] = useState([]);

  // 회원 검색 상태
  const [userSearchKeyword, setUserSearchKeyword] = useState('');
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [showUserSearch, setShowUserSearch] = useState(false);

  // 에러 상태
  const [errors, setErrors] = useState({});

  // 스타일
  const styles = {
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: designTokens.spacing.lg,
    },
    formField: detailModalStyles.formField,
    formLabel: {
      fontSize: '14px',
      fontWeight: '600',
      color: designTokens.colors.text.secondary,
    },
    required: {
      color: designTokens.colors.danger,
      marginLeft: '2px',
    },
    channelGroup: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: designTokens.spacing.xs,
    },
    channelOption: {
      display: 'flex',
      alignItems: 'center',
      gap: designTokens.spacing.xs,
      padding: `${designTokens.spacing.xs} ${designTokens.spacing.sm}`,
      border: `1px solid ${designTokens.colors.border}`,
      borderRadius: designTokens.borderRadius.sm,
      cursor: 'pointer',
      fontSize: '12px',
      transition: 'all 0.2s',
    },
    channelOptionSelected: {
      backgroundColor: designTokens.colors.primary,
      borderColor: designTokens.colors.primary,
      color: '#fff',
    },
    input: {
      width: '100%',
      padding: `${designTokens.spacing.sm} ${designTokens.spacing.md}`,
      border: `1px solid ${designTokens.colors.border}`,
      borderRadius: designTokens.borderRadius.md,
      fontSize: '14px',
      outline: 'none',
    },
    datetimeInput: {
      padding: `${designTokens.spacing.sm} ${designTokens.spacing.md}`,
      border: `1px solid ${designTokens.colors.border}`,
      borderRadius: designTokens.borderRadius.md,
      fontSize: '14px',
      outline: 'none',
      cursor: 'pointer',
    },
    inputError: {
      borderColor: designTokens.colors.danger,
    },
    errorText: {
      fontSize: '12px',
      color: designTokens.colors.danger,
    },
    textarea: {
      width: '100%',
      minHeight: '100px',
      padding: designTokens.spacing.md,
      border: `1px solid ${designTokens.colors.border}`,
      borderRadius: designTokens.borderRadius.md,
      fontSize: '14px',
      resize: 'vertical',
      fontFamily: 'inherit',
      outline: 'none',
    },
    autocompleteContainer: {
      position: 'relative',
    },
    autocompleteDropdown: {
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      backgroundColor: designTokens.colors.surface,
      border: `1px solid ${designTokens.colors.border}`,
      borderRadius: designTokens.borderRadius.md,
      boxShadow: designTokens.shadows.md,
      zIndex: 10,
      maxHeight: '200px',
      overflowY: 'auto',
    },
    autocompleteItem: {
      padding: `${designTokens.spacing.sm} ${designTokens.spacing.md}`,
      cursor: 'pointer',
      fontSize: '14px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      borderBottom: `1px solid ${designTokens.colors.borderLight}`,
    },
    autocompleteItemHover: {
      backgroundColor: '#f8fafc',
    },
    autocompleteInfo: {
      fontSize: '12px',
      color: designTokens.colors.text.muted,
    },
    divider: {
      borderTop: `1px solid ${designTokens.colors.border}`,
      margin: `${designTokens.spacing.md} 0`,
    },
    userSearchSection: {
      backgroundColor: '#f8fafc',
      padding: designTokens.spacing.md,
      borderRadius: designTokens.borderRadius.md,
      border: `1px solid ${designTokens.colors.borderLight}`,
    },
    userSearchTitle: {
      fontSize: '14px',
      fontWeight: '600',
      color: designTokens.colors.text.secondary,
      marginBottom: designTokens.spacing.sm,
      display: 'flex',
      alignItems: 'center',
      gap: designTokens.spacing.sm,
    },
    userSearchInput: {
      display: 'flex',
      alignItems: 'center',
      gap: designTokens.spacing.sm,
      backgroundColor: designTokens.colors.surface,
      border: `1px solid ${designTokens.colors.border}`,
      borderRadius: designTokens.borderRadius.md,
      padding: `${designTokens.spacing.xs} ${designTokens.spacing.md}`,
    },
    userSearchInputField: {
      flex: 1,
      border: 'none',
      outline: 'none',
      fontSize: '14px',
    },
    userSearchResults: {
      marginTop: designTokens.spacing.sm,
      backgroundColor: designTokens.colors.surface,
      border: `1px solid ${designTokens.colors.border}`,
      borderRadius: designTokens.borderRadius.md,
      maxHeight: '150px',
      overflowY: 'auto',
    },
    userItem: {
      padding: `${designTokens.spacing.sm} ${designTokens.spacing.md}`,
      cursor: 'pointer',
      borderBottom: `1px solid ${designTokens.colors.borderLight}`,
      transition: 'background-color 0.2s',
    },
    userItemName: {
      fontWeight: '500',
      marginBottom: '2px',
    },
    userItemInfo: {
      fontSize: '12px',
      color: designTokens.colors.text.muted,
      display: 'flex',
      gap: designTokens.spacing.md,
    },
    selectedUser: {
      backgroundColor: '#dbeafe',
      padding: designTokens.spacing.md,
      borderRadius: designTokens.borderRadius.md,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: designTokens.spacing.sm,
    },
    selectedUserInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: designTokens.spacing.sm,
    },
    removeUser: {
      cursor: 'pointer',
      color: designTokens.colors.text.muted,
      padding: designTokens.spacing.xs,
    },
    actions: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: designTokens.spacing.md,
      marginTop: designTokens.spacing.md,
    },
  };

  // 고객명 자동완성
  const handleCustomerNameChange = async (e) => {
    const value = e.target.value;
    setCustomerName(value);

    if (value.length >= 1) {
      const result = await searchWorkMemoCustomer(value);

      if (typeof result !== 'string' && result?.data) {
        const data = result.data.data || result.data || [];
        setAutocompleteResults(data);
        setShowAutocomplete(data.length > 0);
      } else {
        setShowAutocomplete(false);
      }
    } else {
      setShowAutocomplete(false);
    }
  };

  // 기존 고객 선택 시 해당 메모로 이동
  const handleSelectExistingCustomer = (item) => {
    navigate(`/work-memo/${item.memo_id}`);
  };

  // 회원 검색
  const handleUserSearch = async () => {
    if (userSearchKeyword.length < 1) return;

    const result = await searchUserForMemo(userSearchKeyword);

    if (typeof result !== 'string' && result?.data) {
      const data = result.data.data || result.data || [];
      setUserSearchResults(data);
      setShowUserSearch(true);
    } else {
      setUserSearchResults([]);
      setShowUserSearch(true);
    }
  };

  // 회원 선택
  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setUserSearchKeyword('');
    setShowUserSearch(false);

    // 고객명 자동 채우기
    if (user.user_name && !customerName) {
      setCustomerName(user.user_name);
    }

    // 연락처 자동 채우기
    if (user.phone && !contact) {
      setContact(formatPhoneNumber(user.phone));
    }
  };

  // 회원 선택 해제
  const handleRemoveUser = () => {
    setSelectedUser(null);
  };

  // 유효성 검사
  const validate = () => {
    const newErrors = {};

    if (!channel) {
      newErrors.channel = '채널을 선택해주세요.';
    }
    if (!customerName.trim()) {
      newErrors.customerName = '고객명/상표명을 입력해주세요.';
    }
    if (!content.trim()) {
      newErrors.content = '내용을 입력해주세요.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 저장
  const handleSubmit = async () => {
    if (!validate()) return;

    // 로컬 시간(KST) 그대로 전송 (UTC 변환 없이)
    const formatLocalDateTime = (dateStr) => {
      if (!dateStr) {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const seconds = String(now.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
      }
      // datetime-local은 "2024-12-31T14:30" 형식, 초 추가
      return `${dateStr}:00`;
    };

    const data = {
      channel,
      memo_at: formatLocalDateTime(memoAt),
      customer_name: customerName,
      contact: contact ? removePhoneHyphens(contact) : null,
      content,
      user_pk: selectedUser?.user_pk || null,
    };

    const result = await createWorkMemo(data);

    // 에러 처리
    if (typeof result === 'string') {
      if (result === 'tokenExpired' || result === 'duplicateLogin') {
        return;
      }
      setErrors({ submit: '메모 등록에 실패했습니다.' });
      return;
    }

    onSuccess();
  };

  return (
    <Modal
      title="새 메모 등록"
      onClose={onClose}
      width="550px"
    >
      <div style={styles.form}>
        {/* 채널 선택 */}
        <div style={styles.formField}>
          <label style={styles.formLabel}>
            채널<span style={styles.required}>*</span>
          </label>
          <div style={styles.channelGroup}>
            {CHANNEL_OPTIONS.map(opt => (
              <div
                key={opt.value}
                style={{
                  ...styles.channelOption,
                  ...(channel === opt.value ? styles.channelOptionSelected : {}),
                }}
                onClick={() => setChannel(opt.value)}
              >
                <input
                  type="radio"
                  checked={channel === opt.value}
                  onChange={() => setChannel(opt.value)}
                  style={{ display: 'none' }}
                />
                {opt.label}
              </div>
            ))}
          </div>
          {errors.channel && <span style={styles.errorText}>{errors.channel}</span>}
        </div>

        {/* 마크픽 회원 연결 */}
        <div style={styles.userSearchSection}>
          <div style={styles.userSearchTitle}>
            <FaUser size={14} />
            마크픽 회원 연결 (선택)
          </div>

          {!selectedUser ? (
            <>
              <div style={styles.userSearchInput}>
                <FaSearch size={14} style={{ color: designTokens.colors.text.muted }} />
                <input
                  type="text"
                  style={styles.userSearchInputField}
                  placeholder="회원명, 이메일, 전화번호로 검색..."
                  value={userSearchKeyword}
                  onChange={(e) => setUserSearchKeyword(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleUserSearch()}
                />
                <Button
                  variant="secondary"
                  onClick={handleUserSearch}
                  style={{ padding: `${designTokens.spacing.xs} ${designTokens.spacing.sm}`, fontSize: '12px' }}
                >
                  검색
                </Button>
              </div>

              {showUserSearch && userSearchResults.length > 0 && (
                <div style={styles.userSearchResults}>
                  {userSearchResults.map(user => (
                    <div
                      key={user.user_pk}
                      style={styles.userItem}
                      onClick={() => handleSelectUser(user)}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <div style={styles.userItemName}>
                        {user.user_name} ({user.email})
                      </div>
                      <div style={styles.userItemInfo}>
                        <span>📋 {user.agent_count}건</span>
                        <span>📄 {user.application_count}건</span>
                        <span>💰 {user.payment_count}건</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {showUserSearch && userSearchResults.length === 0 && (
                <div style={{ marginTop: designTokens.spacing.sm, fontSize: '13px', color: designTokens.colors.text.muted, textAlign: 'center' }}>
                  검색 결과가 없습니다
                </div>
              )}
            </>
          ) : (
            <div style={styles.selectedUser}>
              <div style={styles.selectedUserInfo}>
                <FaUser size={14} style={{ color: designTokens.colors.primary }} />
                <div>
                  <div style={{ fontWeight: '500' }}>{selectedUser.user_name}</div>
                  <div style={{ fontSize: '12px', color: designTokens.colors.text.muted }}>
                    {selectedUser.email} · 📋 {selectedUser.agent_count}건 · 📄 {selectedUser.application_count}건 · 💰 {selectedUser.payment_count}건
                  </div>
                </div>
              </div>
              <div
                style={styles.removeUser}
                onClick={handleRemoveUser}
              >
                <FaTimes size={14} />
              </div>
            </div>
          )}
        </div>

        {/* 문의 시간 */}
        <div style={styles.formField}>
          <label style={styles.formLabel}>
            문의 시간
            <span style={{ fontSize: '12px', color: designTokens.colors.text.muted, marginLeft: '8px', fontWeight: 'normal' }}>
              (기본: 현재 시간)
            </span>
          </label>
          <input
            type="datetime-local"
            style={styles.datetimeInput}
            value={memoAt}
            onChange={(e) => setMemoAt(e.target.value)}
            max={getCurrentDateTime()}
          />
        </div>

        {/* 고객명/상표명 */}
        <div style={styles.formField}>
          <label style={styles.formLabel}>
            고객명/상표명<span style={styles.required}>*</span>
          </label>
          <div style={styles.autocompleteContainer}>
            <input
              type="text"
              style={{
                ...styles.input,
                ...(errors.customerName ? styles.inputError : {}),
              }}
              placeholder="고객명 또는 상표명 입력"
              value={customerName}
              onChange={handleCustomerNameChange}
              onBlur={() => setTimeout(() => setShowAutocomplete(false), 200)}
            />
            {showAutocomplete && (
              <div style={styles.autocompleteDropdown}>
                {autocompleteResults.map(item => (
                  <div
                    key={item.memo_id}
                    style={styles.autocompleteItem}
                    onClick={() => handleSelectExistingCustomer(item)}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f8fafc'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                  >
                    <span>{item.customer_name}</span>
                    <span style={styles.autocompleteInfo}>
                      {item.updated_at?.split('T')[0] || ''}
                    </span>
                  </div>
                ))}
                <div style={{ padding: designTokens.spacing.sm, fontSize: '12px', color: designTokens.colors.text.muted, textAlign: 'center' }}>
                  기존 고객 선택 시 해당 메모로 이동합니다
                </div>
              </div>
            )}
          </div>
          {errors.customerName && <span style={styles.errorText}>{errors.customerName}</span>}
        </div>

        {/* 연락처 */}
        <div style={styles.formField}>
          <label style={styles.formLabel}>연락처 (선택)</label>
          <input
            type="text"
            style={styles.input}
            placeholder="010-0000-0000"
            value={contact}
            onChange={(e) => setContact(formatPhoneNumber(e.target.value))}
            maxLength={13}
          />
        </div>

        {/* 내용 */}
        <div style={styles.formField}>
          <label style={styles.formLabel}>
            내용<span style={styles.required}>*</span>
            <span style={{ fontSize: '11px', color: designTokens.colors.text.muted, marginLeft: '8px', fontWeight: 'normal' }}>
              Ctrl+B: 굵게
            </span>
          </label>
          <RichTextEditor
            value={content}
            onChange={setContent}
            placeholder="상담 내용을 입력하세요..."
            minHeight="120px"
            style={errors.content ? { borderColor: designTokens.colors.danger } : {}}
          />
          {errors.content && <span style={styles.errorText}>{errors.content}</span>}
        </div>

        {/* 버튼 */}
        <div style={styles.actions}>
          <Button variant="secondary" onClick={onClose}>
            취소
          </Button>
          <Button variant="primary" onClick={handleSubmit}>
            저장
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default WorkMemoCreateModal;
